import { useMemo, useState } from 'react';
import { useRoadmap } from '../hooks/useRoadmap';
import { signOut } from '../hooks/useAuth';
import HoldingColumn from './HoldingColumn';
import InitiativeModal from './InitiativeModal';
import AppModal from './AppModal';
import type { App, Initiative } from '../lib/types';

export default function Dashboard() {
  const { holdings, apps, initiatives, loading, error, refresh } = useRoadmap(true);

  const [editingInitiative, setEditingInitiative] = useState<
    { app_id: string; initiative?: Initiative } | null
  >(null);
  const [editingApp, setEditingApp] = useState<
    { holding_id: string; app?: App } | null
  >(null);

  const stats = useMemo(() => {
    const total = initiatives.length;
    const inProgress = initiatives.filter((i) => i.status === 'in_progress').length;
    const blocked = initiatives.filter((i) => i.status === 'blocked').length;
    const done = initiatives.filter((i) => i.status === 'done').length;
    const p0 = initiatives.filter((i) => i.priority === 'P0' && i.status !== 'done' && i.status !== 'cancelled').length;
    return { total, inProgress, blocked, done, p0 };
  }, [initiatives]);

  return (
    <div className="min-h-screen px-6 py-6 md:px-10 md:py-8">
      <header className="flex flex-wrap items-center gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="flex -space-x-1.5">
            <span className="w-3 h-3 rounded-full bg-uan shadow-[0_0_12px_theme(colors.uan.DEFAULT)]"></span>
            <span className="w-3 h-3 rounded-full bg-takion shadow-[0_0_12px_theme(colors.takion.DEFAULT)]"></span>
          </div>
          <h1 className="text-xl font-semibold tracking-tight">Roadmap</h1>
          <span className="text-[10px] uppercase tracking-widest text-ink-400 hidden md:inline">
            UAN AI · TAKION
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-4 ml-auto text-xs text-ink-300">
          <Stat label="Total" value={stats.total} />
          <Stat label="En curso" value={stats.inProgress} accent="amber" />
          <Stat label="Bloqueada" value={stats.blocked} accent="rose" />
          <Stat label="P0 abiertos" value={stats.p0} accent="rose" />
          <Stat label="Hechos" value={stats.done} accent="emerald" />
          <button className="btn" onClick={refresh} disabled={loading}>
            {loading ? 'Cargando…' : 'Refrescar'}
          </button>
          <button className="btn" onClick={signOut}>Salir</button>
        </div>
      </header>

      {error && (
        <div className="mb-6 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {holdings.map((h) => (
          <HoldingColumn
            key={h.id}
            holding={h}
            apps={apps.filter((a) => a.holding_id === h.id)}
            initiatives={initiatives}
            onEditInitiative={(app_id, initiative) =>
              setEditingInitiative({ app_id, initiative })
            }
            onAddInitiative={(app_id) => setEditingInitiative({ app_id })}
            onEditApp={(app) => setEditingApp({ holding_id: h.id, app })}
            onAddApp={() => setEditingApp({ holding_id: h.id })}
          />
        ))}
      </div>

      {editingInitiative && (
        <InitiativeModal
          apps={apps}
          initialAppId={editingInitiative.app_id}
          initiative={editingInitiative.initiative}
          onClose={() => setEditingInitiative(null)}
          onSaved={() => {
            setEditingInitiative(null);
            refresh();
          }}
        />
      )}

      {editingApp && (
        <AppModal
          holdingId={editingApp.holding_id}
          app={editingApp.app}
          onClose={() => setEditingApp(null)}
          onSaved={() => {
            setEditingApp(null);
            refresh();
          }}
        />
      )}

      <footer className="mt-10 text-[11px] text-ink-500 text-center">
        Última carga: {new Date().toLocaleString('es-AR')}
      </footer>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: 'amber' | 'rose' | 'emerald' }) {
  const color =
    accent === 'amber' ? 'text-amber-300' :
    accent === 'rose' ? 'text-rose-300' :
    accent === 'emerald' ? 'text-emerald-300' :
    'text-ink-100';
  return (
    <div className="flex items-baseline gap-1.5">
      <span className={`text-base font-semibold ${color}`}>{value}</span>
      <span className="text-[10px] uppercase tracking-wider text-ink-400">{label}</span>
    </div>
  );
}
